import type { components } from '@connected/api-client/api.generated';

import { useMemo } from 'react';

import type { Option } from '@/hooks/use-option';

import { getDisabledKinds, isValidCombination } from '../config/combination-restrictions';

type ElectricalInspectionKind = components['schemas']['ElectricalInspectionKind'];

export type InspectionReportTemplate = {
    id: string;
    name: string;
    inspectionKind: ElectricalInspectionKind;
    selected: boolean;
};

export type RestrictedOption = Option<string> & {
    disabled: boolean;
    inspectionKind: ElectricalInspectionKind;
};

/**
 * Hook to apply combination restrictions to inspection report options.
 *
 * This hook takes a list of templates and returns:
 * - options: Array of options with disabled state based on current selection
 * - isValidSelection: Whether the current selection is valid
 * - selectedKinds: Array of currently selected inspection kinds
 */
export function useCombinationRestrictions(templates: InspectionReportTemplate[]) {
    const selectedKinds = useMemo(() => {
        return templates
            .filter(t => t.selected)
            .map(t => t.inspectionKind);
    }, [templates]);

    const allKinds = useMemo(() => {
        return templates.map(t => t.inspectionKind);
    }, [templates]);

    const disabledKinds = useMemo(() => {
        return getDisabledKinds(selectedKinds, allKinds);
    }, [selectedKinds, allKinds]);

    const options = useMemo((): RestrictedOption[] => {
        return templates.map(template => ({
            value: template.id,
            label: template.name,
            disabled: disabledKinds.has(template.inspectionKind),
            inspectionKind: template.inspectionKind,
        }));
    }, [templates, disabledKinds]);

    const isValidSelection = useMemo(() => {
        return isValidCombination(selectedKinds);
    }, [selectedKinds]);

    return {
        options,
        isValidSelection,
        selectedKinds,
        disabledKinds,
    };
}
